/**
 * Spec 10.2.1 §2.1 — approvals inbox data hooks.
 *
 * Reads the signatory-scoped pending/decided authorisations and exposes
 * approve/decline mutations (approved_via='console_approvals'). Mutations
 * invalidate the pending + decided lists so the inbox and the shell "Needs you"
 * badge refresh together.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

// ---- Shapes (mirror src/engine/ledger-approvals.ts) ----------------------

export interface TxnSummaryLine {
  account_id: string;
  account_no: string;
  title_name: string;
  direction: string;
  amount_minor: number;
  projected_balance_minor: number;
}
export interface TransactionSummary {
  txn_id: string;
  type: string;
  effective_date: string;
  currency: string;
  amount_minor: number | null;
  reason: string | null;
  counterparty_name: string | null;
  lines: TxnSummaryLine[];
  instrument: Record<string, unknown> | null;
  warnings: string[];
}
export interface SubjectFact { k: string; v: string; }
export interface AuthorisationSubjectSummary {
  subject_kind: string;
  subject_id: string;
  title: string;
  gloss: string;
  amount_minor: number | null;
  currency: string | null;
  txn_summary: TransactionSummary | null;
  subject_facts: SubjectFact[];
  overdraw: boolean;
}
export interface PendingAuthorisation {
  id: string;
  ledger_id: string;
  ledger_name: string;
  ledger_bank: string | null;
  subject_kind: string;
  subject_id: string;
  mode: string;
  content_hash: string;
  expires_at: string;
  created_at: string;
  requested_by: { kind: string; id: string; label: string | null };
  summary: AuthorisationSubjectSummary;
}
export interface DecidedAuthorisation {
  id: string;
  ledger_name: string;
  subject_kind: string;
  mode: string;
  content_hash: string;
  created_at: string;
  requested_by: { kind: string; id: string; label: string | null };
  status: string;
  approved_by: string | null;
  declined_reason: string | null;
  approved_at: string | null;
  title: string;
  gloss: string;
}

const PENDING_KEY = ["ledger", "authorisations", "pending"];
const DECIDED_KEY = ["ledger", "authorisations", "decided"];

/** The approvals inbox — pending authorisations the signed-in user may act on. */
export function usePendingAuthorisations(enabled = true) {
  return useQuery({
    queryKey: PENDING_KEY,
    enabled,
    // Live surface: re-poll so expiry countdowns and new requests stay fresh.
    refetchInterval: 30_000,
    staleTime: 10_000,
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ authorisations: PendingAuthorisation[]; ledger_count: number }>("list_pending_authorisations");
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "list_pending_authorisations failed");
      return (r.structuredContent as { authorisations: PendingAuthorisation[]; ledger_count: number }) ?? { authorisations: [], ledger_count: 0 };
    },
  });
}

/** Recently-decided authorisations for the history segment. */
export function useDecidedAuthorisations(enabled = true) {
  return useQuery({
    queryKey: DECIDED_KEY,
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ authorisations: DecidedAuthorisation[] }>("list_decided_authorisations", {});
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "list_decided_authorisations failed");
      return (r.structuredContent as { authorisations: DecidedAuthorisation[] }) ?? { authorisations: [] };
    },
  });
}

function useInvalidateApprovals() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: PENDING_KEY });
    void qc.invalidateQueries({ queryKey: DECIDED_KEY });
    // Approving lands the post, so ledger balances/status change too.
    void qc.invalidateQueries({ queryKey: ["ledger"] });
  };
}

export interface ApproveInput {
  authorisation_id: string;
  subject_kind: string;
  subject_id: string;
  overdraw?: boolean;
}

/**
 * Approve a pending authorisation from the console (out_of_band surface) and,
 * for a transaction subject, COMPLETE it — approving is the whole action ("Approve
 * this payment" → the money moves). The approve commits, then post_ledger_transaction
 * consumes the fresh approval (content hash still matches). A2 admin subjects are
 * executed from their own workbench after approval; overdraw posts need the
 * mandatory explanation, so those are completed from the composer.
 */
export function useApproveAuthorisation() {
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: async (input: ApproveInput) => {
      const approve = await mcpClient.toolsCall("approve_ledger_authorisation", { authorisation_id: input.authorisation_id, approved_via: "console_approvals" });
      if (approve.isError) throw new Error(approve.content?.[0]?.text ?? "approve failed");
      if (input.subject_kind === "transaction" && !input.overdraw) {
        const post = await mcpClient.toolsCall("post_ledger_transaction", { txn_id: input.subject_id });
        if (post.isError) throw new Error(post.content?.[0]?.text ?? "post failed");
      }
      return approve.structuredContent;
    },
    onSuccess: invalidate,
  });
}

/** Decline a pending authorisation with a reason. */
export function useDeclineAuthorisation() {
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: async ({ authorisationId, reason }: { authorisationId: string; reason: string }) => {
      const r = await mcpClient.toolsCall("decline_ledger_authorisation", { authorisation_id: authorisationId, reason });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "decline failed");
      return r.structuredContent;
    },
    onSuccess: invalidate,
  });
}
